import { useCallback, useLayoutEffect } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';

import { useAppState, useIsFocused } from '@sg/garnish';
import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  getOrderPaymentStateConfig,
  isOrderPaymentStatePending,
  useOrderPaymentStateFromLocalStorage,
} from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useOrderPaymentStateQuery } from './GraphQL/OrderPaymentState.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitialOrderPaymentState = () => {
  const router = useRouter();
  const isFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();

  const isOrderPollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore
  const isGiftCardPollingEnabled = useFeatureFlag('CELS-4828-gift-card-payment-state-polling-enabled'); // prettier-ignore
  const isIntentPollingEnabled = isOrderPollingEnabled || isGiftCardPollingEnabled;

  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config'); // prettier-ignore
  const { paymentStateExpirationTolerance } = getOrderPaymentStateConfig(orderPaymentStateConfig); // prettier-ignore

  // ─── Local Storage ─────────────────────────────────────────────────────────
  // Here we rely on local storage instead of cart state because the cart might no longer exist.

  const { orderId, orderType, giftCardAssetId, refetchOrderId } =
    useOrderPaymentStateFromLocalStorage();

  // ─── Queries ───────────────────────────────────────────────────────────────

  const [orderPaymentState, refetchOrderPaymentState] = useOrderPaymentStateQuery({
    pause: !isLoggedIn || !orderId,
    requestPolicy,
    variables: { orderId, orderType },
  });

  const { status, updatedAt } = orderPaymentState.data?.orderPaymentState ?? {};
  const isLoadingOrderPaymentState = orderPaymentState.fetching || orderPaymentState.stale;

  const fetchOrderPaymentState = useCallback(async () => {
    const hasOrderId = await refetchOrderId();
    if (!isLoggedIn || !isIntentPollingEnabled || !hasOrderId) return;
    refetchOrderPaymentState();
  }, [isLoggedIn, isIntentPollingEnabled, refetchOrderId, refetchOrderPaymentState]);

  const redirectIfNeeded = useCallback(async () => {
    if (!isFocused) return;
    if (!isLoggedIn) return;
    if (!isIntentPollingEnabled) return;
    if (isLoadingOrderPaymentState) return;

    const hasOrderId = await refetchOrderId();
    if (!hasOrderId) return;

    // ─── States ────────────────────────────────────────────────────────────────
    // If it is pending, failed or fulfilled then it is unresolved.
    // Pending means it still requires polling for the payment state.
    // Fulfilled or failed means it still needs to show customer feedback.

    const hasFulfilled = status === OrderPaymentStatus.Fulfilled;
    const hasFailed = status === OrderPaymentStatus.Failed;
    const isPending = isOrderPaymentStatePending(
      status,
      Number(updatedAt),
      paymentStateExpirationTolerance,
    );

    if (!hasFulfilled && !hasFailed && !isPending) {
      return;
    }

    // Gravy orders poll the payment intent on the bag screen.
    if (orderType === OrderTypeValues.GravyOrder) {
      router.navigate('/bag');
    }

    // Gift card orders poll the payment intent on the gift card checkout screen.
    if (orderType === OrderTypeValues.GiftCardOrder) {
      router.navigate(`/gift/checkout/${giftCardAssetId}`);
    }
  }, [
    orderType,
    giftCardAssetId,
    isFocused,
    isLoggedIn,
    isIntentPollingEnabled,
    isLoadingOrderPaymentState,
    refetchOrderId,
    status,
    updatedAt,
    paymentStateExpirationTolerance,
    router,
  ]);

  // ─── Refetch On Focus ──────────────────────────────────────────────────────
  // On native, the app will be refocused after the cash app redirect.
  // We must refetch to check if there is a pending payment.

  useAppState({ onVisible: fetchOrderPaymentState });
  useFocusEffect(
    useCallback(() => {
      void fetchOrderPaymentState();
    }, [fetchOrderPaymentState]),
  );

  // ─── Navigate To Bag or Gift Cards ─────────────────────────────────────────
  // When there is an unresolved payment, we need to navigate to bag/gift-cards for polling.

  useLayoutEffect(() => {
    void redirectIfNeeded();
  }, [redirectIfNeeded]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
